import {produce} from 'immer';
import {DeepPartial} from "./types/DeepPartial"

/**
 * Deeply merges a partial object into a base object without modifying the original base object.
 * This function creates a new merged object by recursively combining properties of the partial
 * object into the corresponding properties of the base object.
 *
 * @param {T} base - The original base object to be merged into. This object remains unchanged.
 * @param {DeepPartial<T>} partial - The partial object that contains properties to be merged into the base object.
 *                                    Only properties present in the partial will be merged, leaving the rest of
 *                                    the base object intact.
 *
 * @return {T} A new object that is the result of deeply merging the partial object into the base object.
 */
export function deepMerge<T>(base: T, partial: DeepPartial<T>): T {
    return produce(base, (draft) => {
        // Recursively merge the partial object into the draft
        function merge(obj: any, partialObj: any) {
            for (const key in partialObj) {
                if (Object.prototype.hasOwnProperty.call(partialObj, key)) {
                    const partialValue = partialObj[key];
                    // If both values are objects (not arrays, not Dayjs), recurse to merge deeply
                    if (
                        partialValue != null &&
                        typeof partialValue === 'object' &&
                        !Array.isArray(partialValue) &&
                        !(partialValue as any).$isDayjsObject &&
                        typeof obj[key] === 'object' &&
                        obj[key] != null &&
                        !Array.isArray(obj[key]) &&
                        !(obj[key] as any).$isDayjsObject
                    ) {
                        merge(obj[key], partialValue);
                    } else {
                        // Assign the value directly (handles primitives, arrays, Dayjs, or null)
                        obj[key] = partialValue;
                    }
                }
            }
        }

        merge(draft, partial);
    });
}